<template>
    <multiselect
        :deselect-label="deselectLabel"
        :openDirection="openDirection"
        :value="value"
        :track-by="trackBy"
        :label="label"
        :disabled="disabled"
        :loading="isLoading"
        :placeholder="placeholder"
        :options="filteredItems"
        :searchable="searchable"
        :allowEmpty="allowEmpty"
        :show-labels="false"
        :internal-search="!searchable"
        :class="finalAdditionalClass"
        :customLabel="customLabel"
        :multiple="multiple"
        :name="name"
        @close="handleClose"
        @input="handleInputEvent"
        @open="handleOpen"
        @search-change="debounceSearch"
    >
        <template slot="afterList">
            <div
                v-observe-visibility="{
                    callback: reachedEndOfList,
                }"
                v-if="hasNextPage"
                style="height: 1px"
            />
            <x-loading v-show="isLoading" />
        </template>
        <template v-if="$scopedSlots.option" #option="props">
            <slot name="option" v-bind="props" />
        </template>
        <span slot="noResult"> Data tidak ditemukan. </span>
    </multiselect>
</template>

<script>
import { fulfilledPromise, handleGeneralXhrError } from "../helpers";
import XLoading from "./XLoading.vue";
export default {
    components: { XLoading },
    computed: {
        hasNextPage() {
            const next =
                _.get(this.links, "next") || _.get(this.links, "next_page_url") || _.get(this.meta, "next_page_url");
            return Boolean(next);
        },
        filteredItems() {
            return this.items.filter((item) => {
                return !this.blacklist.includes(item[this.trackBy]);
            });
        },
        finalAdditionalClass() {
            const additions = ["ajax", this.isOpen ? "open" : null].filter((addition) => Boolean(addition));

            if (typeof this.additionalClass === "string") {
                return `${this.additionalClass} ${additions.join(" ")}`;
            }

            if (typeof this.additionalClass === "object") {
                return {
                    ...this.additionalClass,
                    ...additions.reduce((carry, addition) => {
                        return {
                            ...carry,
                            [addition]: true,
                        };
                    }, {}),
                };
            }

            return this.additionalClass;
        },
    },
    created() {
        this.debounceSearch = _.debounce(this.handleSearch.bind(this), 500);
        this.parseData = this.dataParser;
        this.parseMeta = this.metaParser;
        this.parseLinks = this.linksParser;
        this.formatLabel = this.labelFormat;
    },
    data() {
        return {
            meta: {},
            links: {},
            items: [],
            isLoading: false,
            isOpen: false,
            search: null,
        };
    },
    methods: {
        appendDataToItems(data) {
            this.items = [...this.items, ...data];
        },
        fetchData(url = null, append = true) {
            if (url === null) {
                return fulfilledPromise();
            }

            this.isLoading = true;

            return handleGeneralXhrError(
                axios.get(url, {
                    params: {
                        ...this.query,
                        except: this.blacklist,
                        search: this.search,
                        ..._.pickBy({
                            limit: this.search ? Number.MAX_SAFE_INTEGER : null,
                        }),
                    },
                })
            )
                .then((response) => {
                    const parsedData = this.parseData(response);

                    if (append) {
                        this.appendDataToItems(parsedData);
                    } else {
                        this.replaceItems(parsedData);
                    }

                    this.meta = this.parseMeta(response);
                    this.links = this.parseLinks(response);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        handleClose() {
            this.isOpen = false;
        },
        handleInputEvent(data, id) {
            this.$emit("input", data, id);
        },
        handleOpen() {
            if (this.items.length < 1) {
                this.fetchData(this.url, false);
            }

            this.isOpen = true;
        },
        handleSearch(query) {
            this.search = query;
            return this.fetchData(this.url, false);
        },
        reachedEndOfList(reached, entry) {
            const next =
                _.get(this.links, "next") || _.get(this.links, "next_page_url") || _.get(this.meta, "next_page_url");
            if (reached && next) {
                this.fetchData(next);
            }
        },
        replaceItems(data) {
            this.items = data;
        },
    },
    props: {
        name: {
            type: String,
            required: false,
            default: null,
        },
        openDirection: {
            type: String,
            required: false,
            default: "bottom",
        },
        url: {
            type: String,
            required: true,
        },
        label: {
            type: String,
            required: false,
            default: "label",
        },
        trackBy: {
            type: String,
            required: false,
            default: "id",
        },
        deselectLabel: {
            type: String,
            required: false,
            default: "Click to remove",
        },
        allowEmpty: {
            type: Boolean,
            required: false,
            default: false,
        },
        value: {
            type: [Object, Array, String, Number],
            required: false,
            default: null,
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        placeholder: {
            type: String,
            required: false,
            default: "Pilih salah satu item",
        },
        blacklist: {
            type: Array,
            required: false,
            default: () => [],
        },
        searchable: {
            type: Boolean,
            required: false,
            default: true,
        },
        additionalClass: {
            type: [String, Object],
            required: false,
            default: null,
        },
        dataParser: {
            type: Function,
            required: false,
            default: (response) => {
                return _.get(response, "data.data", []);
            },
        },
        metaParser: {
            type: Function,
            required: false,
            default: (response) => {
                return _.get(response, "data.meta", null);
            },
        },
        linksParser: {
            type: Function,
            required: false,
            default: (response) => {
                return _.get(response, "data.links", null);
            },
        },
        customLabel: {
            type: Function,
            required: false,
            default(option) {
                return option[this.label];
            },
        },
        multiple: {
            type: Boolean,
            required: false,
            default: false,
        },
        query: {
            type: Object,
            required: false,
            default: () => ({}),
        },
    },
};
</script>
